import axios from 'axios';
import Papa from 'papaparse';
import assessmentTerms from '../assets/default_config/assessment.json';
import defaultConfigData from '../assets/default_config/config.json';
import diagnosisTerms from '../assets/default_config/diagnosis.json';
import sexTerms from '../assets/default_config/sex.json';
import { fetchConfigGitHubURL, githubRawBaseURL } from './constants';
import { ConfigFile, VocabConfig, ConfigFileStandardizedVariable } from './external_types';
import {
  StandardizedVariables,
  StandardizedTerms,
  StandardizedFormats,
  DataDictionary,
  Columns,
  VariableType,
  DataType,
} from './internal_types';

/**
 * Parses a string value into a number based on a specified format.
 * NOTE: Currently, the parsing logic in this function is heavily tailored towards parsing "Age" values
 * (e.g., handling bounded ages, age ranges, and euro-formatted decimal ages).
 * While the name is generic, the actual implementation may not be suitable for all types of continuous data without modification.
 */
export function parseContinuousValue(value: string, formatId: string | undefined): number | null {
  if (!value || value.trim() === '') return null;
  const str = value.trim();

  let parsed = NaN;
  switch (formatId) {
    case 'nb:FromFloat':
      parsed = Number(str);
      if (Number.isNaN(parsed) || str.toLowerCase().includes('0x')) {
        parsed = NaN;
      }
      break;
    case 'nb:FromInt':
      if (/^[-+]?\d+$/.test(str)) {
        parsed = Number(str);
      } else {
        parsed = NaN;
      }
      break;
    case 'nb:FromEuro': {
      // Step 1: Strict Validation
      // Accept optional negative sign, digits, and an optional comma for decimals. Reject periods.
      // We use \d+ to ensure at least one digit is present before a comma (e.g., "0,5" is valid, ",5" is invalid).
      const isValidEuroFormat = /^-?\d+(,\d+)?$/.test(str);

      if (!isValidEuroFormat) {
        parsed = NaN;
        break;
      }

      // Step 2: JavaScript Conversion
      // JS Number() cannot parse commas, so we translate the valid string to a JS-readable float
      const jsReadableString = str.replace(',', '.');
      parsed = Number(jsReadableString);
      break;
    }
    case 'nb:FromBounded': {
      // Must contain a bounding character (+, <, >, =)
      if (!/[+<>=]/g.test(str)) {
        parsed = NaN;
        break;
      }
      // Strip trailing and leading bounding characters
      const stripped = str.replace(/^[+<>=]+|[+<>=]+$/g, '');
      // Strictly enforce integers for bounded values
      if (/^[-+]?\d+$/.test(stripped)) {
        parsed = Number(stripped);
      } else {
        parsed = NaN;
      }
      break;
    }
    case 'nb:FromRange': {
      // Use regex to carefully extract two potentially negative numbers separated by a dash.
      // E.g., "10-20", "-10-20", "-10--20", "10.5 - 20.5"
      // Match group 1: first number. Match group 2: second number.
      const match = str.match(/^(-?\d+(?:\.\d+)?|-?\.\d+)\s*-\s*(-?\d+(?:\.\d+)?|-?\.\d+)$/);
      if (match) {
        const p0 = match[1];
        const p1 = match[2];
        parsed = (Number(p0) + Number(p1)) / 2;
      }
      break;
    }
    case 'nb:FromISO8601': {
      const pvalue = str.toUpperCase().startsWith('P')
        ? str.toUpperCase()
        : `P${str.toUpperCase()}`;
      // Basic strict ISO8601 duration regex matching subset of what python's isodate handles
      const isoRegex =
        /^P(?:([\d.]+)Y)?(?:([\d.]+)M)?(?:([\d.]+)W)?(?:([\d.]+)D)?(?:T(?:([\d.]+)H)?(?:([\d.]+)M)?(?:([\d.]+)S)?)?$/;
      const match = pvalue.match(isoRegex);
      if (match && pvalue !== 'P') {
        const years = match[1] ? parseFloat(match[1]) : 0;
        const months = match[2] ? parseFloat(match[2]) : 0;
        parsed = years + months / 12;
      }
      break;
    }
    default:
      // Unrecognized format
      return null;
  }

  return Number.isNaN(parsed) ? null : parsed;
}

export interface ContinuousValidationResult {
  invalidValues: string[];
  invalidCount: number;
  validCount: number;
  min: number | null;
  max: number | null;
}

export function validateContinuousValues(
  values: string[],
  missingValues: string[],
  formatId: string | undefined | null
): ContinuousValidationResult | null {
  if (!formatId) return null;

  const validValues: number[] = [];
  const invalidValues: string[] = [];

  values.forEach((val) => {
    if (missingValues.includes(val)) return;

    const parsed = parseContinuousValue(val, formatId);
    if (parsed !== null && !Number.isNaN(parsed)) {
      validValues.push(parsed);
    } else {
      invalidValues.push(val);
    }
  });

  if (validValues.length === 0 && invalidValues.length === 0) return null;

  const min = validValues.length > 0 ? Math.min(...validValues) : null;
  const max = validValues.length > 0 ? Math.max(...validValues) : null;

  return {
    invalidValues,
    invalidCount: invalidValues.length,
    validCount: validValues.length,
    min,
    max,
  };
}

export async function fetchAvailableConfigs(): Promise<string[]> {
  try {
    const response = await axios.get(fetchConfigGitHubURL);
    const data = response.data as { type: string; name: string }[];
    const dirs = data.filter((item) => item.type === 'dir');
    return dirs.map((dir) => dir.name).sort();
  } catch (error) {
    // TODO: show a notif error
    // Return a default config option when remote fetching fails
    return ['Neurobagel'];
  }
}

// Helper function to load config from a given path
async function loadConfigFromPath(
  configPath: string
): Promise<{ config: ConfigFile; termsData: Record<string, VocabConfig[]> }> {
  const configResponse = await axios.get(configPath);
  const configArray = configResponse.data;
  const config = configArray[0];

  // Find all unique terms_file values in standardized_variables
  const termFiles = new Set<string>();
  const variables = config.standardized_variables;

  variables.forEach((variable: ConfigFileStandardizedVariable) => {
    if (variable.terms_file) {
      termFiles.add(variable.terms_file);
    }
  });

  // Load all term files
  const termsData: Record<string, VocabConfig[]> = {};
  const baseUrl = configPath.replace('/config.json', '');

  await Promise.all(
    Array.from(termFiles).map(async (file) => {
      try {
        const res = await axios.get(`${baseUrl}/${file}`);
        termsData[file] = res.data;
      } catch (e) {
        // TODO: show notif error for remote
        termsData[file] = [];
      }
    })
  );

  return { config, termsData };
}

export async function fetchConfig(
  selectedConfig: string
): Promise<{ config: ConfigFile; termsData: Record<string, VocabConfig[]> }> {
  try {
    return await loadConfigFromPath(`${githubRawBaseURL}${selectedConfig}/config.json`);
  } catch (error) {
    // TODO: show a notif error
    // Fallback to default config when remote fetching fails
    try {
      const config = (defaultConfigData as ConfigFile[])[0];
      const termsData: Record<string, VocabConfig[]> = {
        'assessment.json': assessmentTerms as VocabConfig[],
        'diagnosis.json': diagnosisTerms as VocabConfig[],
        'sex.json': sexTerms as VocabConfig[],
      };
      return { config, termsData };
    } catch (fallbackError) {
      return { config: {} as ConfigFile, termsData: {} };
    }
  }
}

export function convertStandardizedVariables(
  variables: ConfigFileStandardizedVariable[],
  namespacePrefix: string
): StandardizedVariables {
  return variables.reduce((acc, variable) => {
    const identifier = `${namespacePrefix}:${variable.id}`;
    const { id, name, terms_file: termsFile, formats: rawFormats, ...rest } = variable;
    return {
      ...acc,
      [identifier]: {
        id: identifier,
        name,
        ...rest,
      },
    };
  }, {} as StandardizedVariables);
}

export function convertStandardizedTerms(
  termsData: Record<string, VocabConfig[]>,
  variables: ConfigFileStandardizedVariable[],
  variableNamespacePrefix: string
): StandardizedTerms {
  return Object.entries(termsData).reduce((acc, [fileName, vocabsArray]) => {
    const newTerms = vocabsArray.flatMap((vocab) => {
      const termsNamespace = vocab.namespace_prefix;
      return vocab.terms.map((term) => {
        const termIdentifier = `${termsNamespace}:${term.id}`;
        const { id, name, abbreviation, ...restTermFields } = term;

        const parentVariable = variables.find((v) => v.terms_file === fileName);
        const standardizedVariableId = parentVariable
          ? `${variableNamespacePrefix}:${parentVariable.id}`
          : '';

        const isCollection = parentVariable?.variable_type === VariableType.collection;

        const termAbbreviation = isCollection ? abbreviation || undefined : undefined;

        return {
          [termIdentifier]: {
            standardizedVariableId,
            id: termIdentifier,
            label: name,
            abbreviation: termAbbreviation,
            ...restTermFields,
            collectionCreatedAt: undefined,
          },
        };
      });
    });

    return {
      ...acc,
      ...Object.assign({}, ...newTerms),
    };
  }, {} as StandardizedTerms);
}

export function convertStandardizedFormats(
  variables: ConfigFileStandardizedVariable[],
  namespacePrefix: string
): StandardizedFormats {
  return variables.reduce((acc, variable) => {
    if (!variable.formats) {
      return acc;
    }

    const standardizedVariableId = `${namespacePrefix}:${variable.id}`;
    const newFormats = variable.formats.map((format) => {
      const formatIdentifier = `${namespacePrefix}:${format.id}`;
      const { id, name, ...restFormatFields } = format;
      return {
        [formatIdentifier]: {
          standardizedVariableId,
          identifier: formatIdentifier,
          label: name,
          ...restFormatFields,
        },
      };
    });

    return {
      ...acc,
      ...Object.assign({}, ...newFormats),
    };
  }, {} as StandardizedFormats);
}

export async function readFile(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const content = e.target?.result as string;
      resolve(content);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsText(file);
  });
}

export function parseTsvContent(content: string): { headers: string[]; data: string[][] } {
  if (!content) return { headers: [], data: [] };

  // TODO: consider validating the table against our schema first,
  // simply skipping empty rows here may cause downstream problems,
  // see: https://github.com/neurobagel/annotation-tool/issues/142
  const result = Papa.parse<string[]>(content, {
    delimiter: '\t',
    skipEmptyLines: true,
  });

  const rows = (result.data || []) as string[][];
  const headers = rows[0];
  const data = rows.slice(1);

  return { headers, data };
}

/**
 * Returns a copy of the column with data type dependent fields updated.
 * Safe to use with Immer since the original object remains untouched.
 */
interface ColumnDataShape {
  dataType?: DataType | null;
  levels?: { [key: string]: { description: string; standardizedTerm: string } } | null;
  units?: string;
}

interface LevelMap {
  [key: string]: { description: string; standardizedTerm: string };
}

interface BuildCategoricalLevelsOptions {
  column: {
    allValues?: string[];
    missingValues?: string[];
  };
  columnData: DataDictionary[string];
  standardizedTerms: StandardizedTerms;
}

export function buildCategoricalLevels({
  column,
  columnData,
  standardizedTerms,
}: BuildCategoricalLevelsOptions): LevelMap {
  const initialLevels: LevelMap = {};
  Array.from(new Set(column.allValues ?? [])).forEach((value) => {
    initialLevels[value] = { description: '', standardizedTerm: '' };
  });

  const finalLevels: LevelMap = {};
  Object.entries(initialLevels).forEach(([value, level]) => {
    const dictLevel = columnData.Levels?.[value];
    const annotationLevel = columnData.Annotations?.Levels?.[value];
    const standardizedTerm =
      annotationLevel?.TermURL && standardizedTerms[annotationLevel.TermURL]
        ? annotationLevel.TermURL
        : level.standardizedTerm;

    const description =
      typeof dictLevel === 'string' ? dictLevel : dictLevel?.Description || level.description;

    finalLevels[value] = {
      description,
      standardizedTerm,
    };
  });
  return finalLevels;
}

export function applyDataTypeToColumn<T extends ColumnDataShape>(
  column: T,
  dataType: DataType | null,
  allValues: string[]
): T {
  const updatedColumn: T = {
    ...column,
    dataType,
  };

  if (dataType === DataType.categorical) {
    const uniqueValues = Array.from(new Set(allValues));

    if (!column.levels) {
      const newLevels: { [key: string]: { description: string; standardizedTerm: string } } = {};
      uniqueValues.forEach((value) => {
        newLevels[value] = { description: '', standardizedTerm: '' };
      });
      updatedColumn.levels = newLevels;
    }

    delete updatedColumn.units;
  } else if (dataType === DataType.continuous) {
    if (updatedColumn.units === undefined) {
      updatedColumn.units = '';
    }
    delete updatedColumn.levels;
  } else {
    delete updatedColumn.levels;
    delete updatedColumn.units;
  }

  return updatedColumn;
}

export function applyDataDictionaryToColumns(
  columns: Columns,
  dataDictionary: DataDictionary,
  standardizedVariables: StandardizedVariables,
  standardizedTerms: StandardizedTerms,
  standardizedFormats: StandardizedFormats
): Columns {
  return Object.entries(dataDictionary).reduce((updatedColumns, [columnName, columnData]) => {
    const matchingColumnEntry = Object.entries(updatedColumns).find(
      ([_, column]) => column.name === columnName
    );

    if (!matchingColumnEntry) return updatedColumns;

    const [columnId] = matchingColumnEntry;
    const column = { ...updatedColumns[columnId] };

    if (columnData.Description) {
      column.description = columnData.Description;
    }

    if (columnData.Annotations?.MissingValues) {
      const uniqueValues = new Set(column.allValues ?? []);
      column.missingValues = columnData.Annotations.MissingValues.filter((value) =>
        uniqueValues.has(value)
      );
    }

    let variableType: VariableType | undefined;
    if (columnData.Annotations?.IsAbout?.TermURL) {
      const standardizedVariableId = columnData.Annotations.IsAbout.TermURL;
      if (standardizedVariables[standardizedVariableId]) {
        column.standardizedVariable = standardizedVariableId;
        variableType = standardizedVariables[standardizedVariableId].variable_type;
      }
    } else if (column.standardizedVariable && standardizedVariables[column.standardizedVariable]) {
      variableType = standardizedVariables[column.standardizedVariable].variable_type;
    }

    if (!variableType && columnData.Annotations?.VariableType) {
      variableType = columnData.Annotations.VariableType;
    }

    let dataType: DataType | null | undefined = column.dataType;
    if (variableType === VariableType.categorical) {
      dataType = DataType.categorical;
    } else if (variableType === VariableType.continuous) {
      dataType = DataType.continuous;
    } else if (variableType === VariableType.identifier) {
      dataType = undefined;
    } else if (columnData.Levels) {
      dataType = DataType.categorical;
    } else if (columnData.Units !== undefined) {
      dataType = DataType.continuous;
    } else if (variableType === VariableType.collection) {
      dataType = undefined;
    }

    column.dataType = dataType;

    switch (dataType) {
      case DataType.categorical:
        column.levels = buildCategoricalLevels({ column, columnData, standardizedTerms });
        break;

      case DataType.continuous:
        if (columnData.Units !== undefined) {
          column.units = columnData.Units;
        }

        if (columnData.Annotations?.Format?.TermURL) {
          const formatId = columnData.Annotations.Format.TermURL;
          if (standardizedFormats[formatId]) {
            column.format = formatId;
          }
        }
        break;

      default:
        break;
    }

    if (variableType === VariableType.collection && columnData.Annotations?.IsPartOf?.TermURL) {
      const termId = columnData.Annotations.IsPartOf.TermURL;
      if (standardizedTerms[termId]) {
        column.isPartOf = termId;
      }
    }

    return {
      ...updatedColumns,
      [columnId]: column,
    };
  }, columns);
}
